import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { CURRENCIES, api } from "../lib/api";
import DemoBanner from "./DemoBanner";
import InstallAppButton from "./InstallAppButton";
import ContactSupport from "./ContactSupport";
import BMVInfo from "./BMVInfo";
import KycAmlInfo from "./KycAmlInfo";
import ReddPlusInfo from "./ReddPlusInfo";
import LegalInfo from "./LegalInfo";

const LANGS = [
  { code: "en", label: "EN" },
  { code: "pt", label: "PT" },
  { code: "es", label: "ES" },
  { code: "fr", label: "FR" },
  { code: "ar", label: "AR" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [stripeMode, setStripeMode] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") {
      setStripeMode(null);
      return;
    }
    api.get("/payments/config")
      .then((r) => setStripeMode(r.data?.mode || "unknown"))
      .catch(() => setStripeMode("unknown"));
  }, [user?.role]);

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("gb_lang", code);
    document.documentElement.dir = code === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = code;
  };

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide font-sans hover:text-ink transition-colors ${isActive ? "text-ink" : "text-ink2"}`;

  return (
    <div className="min-h-screen bg-bg">
      <DemoBanner />
      <header className="sticky top-0 z-40 bg-bg/80 backdrop-blur-xl border-b border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-link">
            <img
              src="https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/xeso8axu_image.png"
              alt="GreenBelt"
              className="h-9 w-9 object-cover rounded-sm"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-5 lg:gap-7 whitespace-nowrap ml-8 lg:ml-12">
            <NavLink to="/marketplace" className={linkCls} data-testid="nav-marketplace">{t("nav.marketplace")}</NavLink>
            <NavLink to="/registry" className={linkCls} data-testid="nav-registry">{t("nav.registry")}</NavLink>
            <NavLink to="/methodology" className={linkCls} data-testid="nav-methodology">{t("nav.methodology")}</NavLink>
            <NavLink to="/insights" className={linkCls} data-testid="nav-insights">{t("nav.insights")}</NavLink>
            {user && <NavLink to="/portfolio" className={linkCls} data-testid="nav-portfolio">{t("nav.portfolio")}</NavLink>}
            {user && <NavLink to="/alerts" className={linkCls} data-testid="nav-alerts">{t("nav.alerts")}</NavLink>}
            {user && <NavLink to="/mitigate" className={linkCls} data-testid="nav-mitigate">{t("nav.mitigate")}</NavLink>}
            {user && <NavLink to="/wallet" className={linkCls} data-testid="nav-wallet">{t("nav.wallet")}</NavLink>}
            {user?.role === "producer" && <NavLink to="/producer" className={linkCls} data-testid="nav-producer">{t("nav.producer", "Producer")}</NavLink>}
            {user?.role === "admin" && <NavLink to="/admin" className={linkCls} data-testid="nav-admin">{t("nav.admin")}</NavLink>}
            {user?.role === "admin" && <NavLink to="/admin/dashboard" className={linkCls} data-testid="nav-dashboard">{t("nav.dashboard", "Dashboard")}</NavLink>}
            {user?.role === "admin" && <NavLink to="/admin/desk" className={linkCls} data-testid="nav-desk">{t("nav.desk")}</NavLink>}
            {user?.role === "admin" && <NavLink to="/admin/bulk" className={linkCls} data-testid="nav-bulk">{t("nav.bulk", "Bulk")}</NavLink>}
            {user?.role === "admin" && <NavLink to="/admin/producers" className={linkCls} data-testid="nav-admin-producers">{t("nav.producers", "Producers")}</NavLink>}
            {user?.role === "admin" && <NavLink to="/compliance" className={linkCls} data-testid="nav-compliance">{t("nav.compliance")}</NavLink>}
          </nav>

          <div className="flex items-center gap-2">
            <select
              data-testid="language-switcher"
              value={i18n.language}
              onChange={(e) => changeLang(e.target.value)}
              className="bg-transparent border border-line px-2 py-1.5 text-xs font-mono tracking-wider rounded-none focus:outline-none focus:border-ink"
              aria-label={t("lang")}
            >
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <select
              data-testid="currency-switcher"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="bg-transparent border border-line px-2 py-1.5 text-xs font-mono tracking-wider rounded-none focus:outline-none focus:border-ink"
            >
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <InstallAppButton variant="compact" className="hidden lg:inline-flex" />
            <ContactSupport variant="header" className="hidden lg:inline-flex" />
            {user ? (
              <div className="flex items-center gap-3">
                {stripeMode && (
                  <span
                    data-testid="stripe-mode-badge"
                    title="Stripe mode (admin only)"
                    className={`hidden md:inline px-2 py-1 text-[10px] font-mono uppercase tracking-wider border ${
                      stripeMode === "live"
                        ? "bg-leaf text-white border-leaf"
                        : stripeMode === "test"
                        ? "bg-amber-500 text-white border-amber-500"
                        : "bg-muted text-ink2 border-line"
                    }`}
                  >
                    · {stripeMode} ·
                  </span>
                )}
                <span className="hidden lg:inline text-xs font-mono text-ink2" data-testid="user-email">{user.email}</span>
                <button onClick={async () => { await logout(); nav("/"); }} className="btn-outline !py-1.5 !px-4 !text-xs" data-testid="logout-btn">
                  {t("nav.logout")}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="btn-outline !py-1.5 !px-4 !text-xs" data-testid="login-link">{t("nav.login")}</Link>
                <Link to="/register" className="btn-primary !py-1.5 !px-4 !text-xs" data-testid="register-link">{t("nav.register")}</Link>
              </div>
            )}
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-line mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img
                src="https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/xeso8axu_image.png"
                alt="GreenBelt"
                className="h-10 w-10 object-cover rounded-sm"
              />
              <div className="font-serif text-2xl">GreenBelt</div>
            </div>
            <p className="text-sm text-ink2 max-w-xs">{t("footer.tagline")}</p>
          </div>
          <div>
            <div className="label-tag mb-3">{t("footer.markets")}</div>
            <ul className="space-y-2 text-sm text-ink2">
              <li><ReddPlusInfo /></li><li>Renewable Energy</li><li>Engineered Removals</li><li>Community</li>
            </ul>
          </div>
          <div>
            <div className="label-tag mb-3">{t("footer.standards")}</div>
            <ul className="space-y-2 text-sm text-ink2">
              <li><BMVInfo /></li>
            </ul>
          </div>
          <div>
            <div className="label-tag mb-3">{t("footer.legal")}</div>
            <ul className="space-y-2 text-sm text-ink2">
              <li><LegalInfo ns="legal_terms" testid="terms" /></li>
              <li><LegalInfo ns="legal_privacy" testid="privacy" /></li>
              <li><KycAmlInfo /></li>
              <li><LegalInfo ns="legal_disclosures" testid="disclosures" /></li>
            </ul>
          </div>
          <ContactSupport variant="footer" />
        </div>
        <div className="border-t border-line py-5 text-center text-xs font-mono text-ink2 space-y-1">
          <div>© {new Date().getFullYear()} {t("footer.copy")}</div>
          <div data-testid="footer-ksa-license">{t("footer.ksa_registration")}</div>
        </div>
      </footer>
    </div>
  );
}
